import React from 'react'
import type { DraggableAttributes, DraggableSyntheticListeners } from '@dnd-kit/core'
import { ChevronDown, ChevronRight, Bookmark, CircleDot, Zap, CornerDownRight, Check, MoreHorizontal, AlertTriangle, Flag } from 'lucide-react'
import { IssueActionsMenu } from './IssueActionsMenu'
import type { Issue, WorkflowStatus } from '../api'
import type { UserOption } from '../../../shared/hooks/useUserOptions'

export const LIST_GRID_CLASS =
  'grid grid-cols-[36px_28px_minmax(200px,1fr)_110px_100px_110px_85px_110px_120px_110px_140px] items-center'

export function IssueTypeIcon({ type, className = '' }: { type: string; className?: string }) {
  switch (type) {
    case 'story':
      return (
        <span title="Story" className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-[3px] bg-[#65ba43] text-white shadow-xs ${className}`}>
          <Bookmark className="h-2.5 w-2.5 fill-current" />
        </span>
      )
    case 'bug':
      return (
        <span title="Bug" className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[#e5493a] text-white shadow-xs ${className}`}>
          <CircleDot className="h-2.5 w-2.5" />
        </span>
      )
    case 'epic':
      return (
        <span title="Epic" className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-[3px] bg-[#904ee2] text-white shadow-xs ${className}`}>
          <Zap className="h-2.5 w-2.5 fill-current" />
        </span>
      )
    case 'subtask':
      return (
        <span title="Subtask" className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-[3px] bg-[#2684ff] text-white shadow-xs ${className}`}>
          <CornerDownRight className="h-2.5 w-2.5 stroke-[2.5]" />
        </span>
      )
    case 'task':
    default:
      return (
        <span title="Task" className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-[3px] bg-[#388BFF] text-white shadow-xs ${className}`}>
          <Check className="h-2.5 w-2.5 stroke-[3]" />
        </span>
      )
  }
}

export const PRIORITY_TONES: Record<string, string> = {
  critical: 'bg-[#DEEBFF] text-[#0052CC]',
  high: 'bg-[#DEEBFF] text-[#0052CC]',
  medium: 'bg-[#DEEBFF] text-[#0052CC]',
  low: 'bg-[#DEEBFF] text-[#0052CC]',
}

export const STATUS_TONES: Record<string, string> = {
  todo: 'bg-[#EBECF0] text-[#42526E]',
  in_progress: 'bg-[#DEEBFF] text-[#0052CC]',
  done: 'bg-[#E3FCEF] text-[#006644]',
}

export interface ListRowProps {
  issue: Issue
  statuses: WorkflowStatus[]
  members: UserOption[]
  selected?: boolean
  onToggleSelect?: (checked: boolean, shiftKey?: boolean) => void
  onOpen: () => void
  onUpdate?: (payload: Partial<Issue>) => void
  indent?: boolean
  subtaskCount?: number
  subtaskDoneCount?: number
  expanded?: boolean
  onToggleExpand?: () => void
  dragHandleProps?: { attributes: DraggableAttributes; listeners: DraggableSyntheticListeners }
  rowRef?: (node: HTMLElement | null) => void
  rowStyle?: React.CSSProperties
  onDelete?: (id: string) => void
  onLogTime?: (id: string) => void
  allStatuses?: Array<{ id: string; name: string; category?: string }>
  visibleColumns?: {
    type?: boolean
    work?: boolean
    labels?: boolean
    assignee?: boolean
    reporter?: boolean
    priority?: boolean
    status?: boolean
    updated?: boolean
    dueDate?: boolean
    created?: boolean
  }
  columnWidths?: Record<string, number>
}

export function ListRow({
  issue,
  statuses,
  members,
  selected = false,
  onToggleSelect,
  onOpen,
  onUpdate,
  indent = false,
  subtaskCount = 0,
  subtaskDoneCount = 0,
  expanded = false,
  onToggleExpand,
  dragHandleProps,
  rowRef,
  rowStyle,
  onDelete,
  onLogTime,
  allStatuses = [],
  visibleColumns,
  columnWidths,
}: ListRowProps) {
  const [editingTitle, setEditingTitle] = React.useState(false)
  const [titleDraft, setTitleDraft] = React.useState(issue.title)
  const titleRef = React.useRef<HTMLInputElement | null>(null)

  React.useEffect(() => {
    setTitleDraft(issue.title)
  }, [issue.title])

  React.useEffect(() => {
    if (editingTitle) {
      titleRef.current?.focus()
      titleRef.current?.select()
    }
  }, [editingTitle])

  const status = statuses.find((s) => s.id === issue.status)

  const assignee = members.find((m) => m.id === issue.assignee_id)
  const assigneeDisplayName = assignee?.name || assignee?.display_name?.split(' — ')[0] || assignee?.display_name
  const assigneeName = assigneeDisplayName || 'Unassigned'

  const reporter = members.find((m) => m.id === issue.reporter_id)
  const reporterDisplayName = reporter?.name || reporter?.display_name?.split(' — ')[0] || reporter?.display_name
  const reporterName = reporterDisplayName || '—'

  const rawPriority = issue.priority || 'medium'
  const priorityLabel = rawPriority.charAt(0).toUpperCase() + rawPriority.slice(1)

  const statusCategory = status?.category ?? issue.status_category ?? 'todo'
  const statusName =
    status?.name ??
    (statusCategory === 'done' ? 'Done' : statusCategory === 'in_progress' ? 'In Progress' : 'To Do')

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation()
    const shiftKey = (e.nativeEvent as any).shiftKey ?? false
    onToggleSelect?.(e.target.checked, shiftKey)
  }

  const handleClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement | null
    if (target?.closest('input, select')) return
    const btn = target?.closest('button')
    if (btn) return
    onOpen()
  }

  const cols = {
    type: true,
    work: true,
    labels: true,
    assignee: true,
    reporter: true,
    priority: true,
    status: true,
    updated: true,
    dueDate: true,
    created: false,
    ...visibleColumns,
  }

  // Format updated date like "Jul 22, 2026, 8:25 PM"
  let updatedText = 'Jul 22, 2026, 8:25 PM'
  const rawUpdated = issue.updated_at || (issue as any).updatedAt || (issue as any).updated || issue.created_at
  if (rawUpdated) {
    const d = new Date(rawUpdated)
    if (!isNaN(d.getTime())) {
      const month = d.toLocaleDateString('en-US', { month: 'short' })
      const day = d.getDate()
      const year = d.getFullYear()
      const time = d.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
      updatedText = `${month} ${day}, ${year}, ${time}`
    }
  }

  // Format due date like "Aug 5, 2026" with overdue status
  let dueDateText = '—'
  let isOverdue = false
  if (issue.due_date) {
    const d = new Date(issue.due_date)
    if (!isNaN(d.getTime())) {
      const month = d.toLocaleDateString('en-US', { month: 'short' })
      const day = d.getDate()
      const year = d.getFullYear()
      dueDateText = `${month} ${day}, ${year}`
      if (d.getTime() < Date.now() && statusCategory !== 'done') {
        isOverdue = true
      }
    }
  }

  const createdDate = issue.created_at
    ? new Date(issue.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '—'

  const labels = issue.tags ?? issue.labels ?? []
  const availableStatuses = allStatuses.length > 0 ? allStatuses : statuses

  return (
    <tr
      ref={rowRef}
      style={rowStyle}
      {...dragHandleProps?.attributes}
      onClick={handleClick}
      className={`border-b border-[#EBECF0] hover:bg-[#F4F5F7] cursor-pointer transition-colors dark:border-slate-800/80 dark:hover:bg-slate-800/50 ${
        selected
          ? 'bg-[#F4F5F7] dark:bg-slate-800/60'
          : indent
          ? 'bg-gray-50/40 dark:bg-slate-800/30'
          : 'bg-white dark:bg-slate-900'
      }`}
    >
      {/* 1. Checkbox */}
      <td className="w-10 pl-4 pr-1 py-3 text-center align-middle">
        <input
          type="checkbox"
          checked={selected}
          onChange={handleCheckboxChange}
          className="h-4 w-4 rounded-[3px] border border-[#C1C7D0] text-[#0052CC] focus:ring-0 focus:outline-none cursor-pointer bg-white dark:bg-slate-800 dark:border-slate-600"
        />
      </td>

      {/* 2. Type */}
      {cols.type && (
        <td
          style={{
            width: columnWidths?.type ? `${columnWidths.type}px` : undefined,
            minWidth: columnWidths?.type ? `${columnWidths.type}px` : undefined,
          }}
          className="px-3 py-3 align-middle"
        >
          <IssueTypeIcon type={issue.type} />
        </td>
      )}

      {/* 3. Work with inline title edit */}
      {cols.work && (
        <td
          style={{
            width: columnWidths?.work ? `${columnWidths.work}px` : undefined,
            minWidth: columnWidths?.work ? `${columnWidths.work}px` : undefined,
          }}
          className={`px-3 py-3 align-middle ${indent ? 'pl-6' : ''}`}
        >
          <div className="flex items-center gap-2.5">
            {subtaskCount > 0 ? (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onToggleExpand?.()
                }}
                className="inline-flex items-center justify-center text-gray-400 hover:text-gray-700 dark:hover:text-slate-200 mr-0.5 shrink-0"
                title={expanded ? 'Collapse subtasks' : 'Expand subtasks'}
              >
                {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              </button>
            ) : (
              <span className="w-3.5 shrink-0" />
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                {editingTitle ? (
                  <input
                    ref={titleRef}
                    value={titleDraft}
                    onChange={(e) => setTitleDraft(e.target.value)}
                    onBlur={() => {
                      const next = titleDraft.trim()
                      if (next && next !== issue.title) onUpdate?.({ title: next })
                      setEditingTitle(false)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const next = titleDraft.trim()
                        if (next && next !== issue.title) onUpdate?.({ title: next })
                        setEditingTitle(false)
                      }
                      if (e.key === 'Escape') {
                        setTitleDraft(issue.title)
                        setEditingTitle(false)
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="h-7 w-full rounded-md border border-blue-400 bg-white px-2 text-[13px] text-[#172B4D] outline-none focus:ring-1 focus:ring-blue-500 dark:bg-slate-900 dark:text-slate-100"
                  />
                ) : (
                  <span
                    onClick={(e) => {
                      e.stopPropagation()
                      onOpen()
                    }}
                    onDoubleClick={(e) => {
                      e.stopPropagation()
                      setTitleDraft(issue.title)
                      setEditingTitle(true)
                    }}
                    title="Click to view details (double-click to edit)"
                    className="text-[13px] font-medium text-[#172B4D] hover:text-[#0052CC] hover:underline dark:text-slate-100 dark:hover:text-blue-400 truncate cursor-pointer"
                  >
                    {issue.title}
                  </span>
                )}
              </div>
              {issue.flagged && (
                <span
                  title="Flagged"
                  className="flex items-center justify-center text-orange-500 hover:text-orange-600 cursor-pointer"
                >
                  <Flag className="h-3.5 w-3.5 fill-current" />
                </span>
              )}
              {subtaskCount > 0 && !expanded && (
                <div className="mt-1 flex items-center gap-1.5">
                  <div className="flex-1 h-1 rounded-full bg-gray-200 dark:bg-slate-700 overflow-hidden max-w-[120px]">
                    <div
                      className="h-full bg-emerald-500 rounded-full transition-all"
                      style={{ width: `${(subtaskDoneCount / subtaskCount) * 100}%` }}
                    />
                  </div>
                  <span className="text-[10px] text-gray-500 dark:text-slate-400 font-medium tabular-nums">
                    {subtaskDoneCount}/{subtaskCount}
                  </span>
                </div>
              )}
              {subtaskCount > 0 && expanded && (
                <span className="text-[10px] text-gray-500 dark:text-slate-400 font-medium mt-0.5">
                  {subtaskDoneCount}/{subtaskCount} subtasks done
                </span>
              )}
            </div>
          </div>
        </td>
      )}

      {/* 4. Labels */}
      {cols.labels && (
        <td
          style={{
            width: columnWidths?.labels ? `${columnWidths.labels}px` : undefined,
            minWidth: columnWidths?.labels ? `${columnWidths.labels}px` : undefined,
          }}
          className="px-3 py-2 align-middle hidden lg:table-cell"
        >
          <div className="flex flex-wrap gap-1">
            {labels.slice(0, 2).map((lbl, idx) => (
              <span
                key={idx}
                className="inline-block rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 border border-blue-200/60 dark:border-blue-800/60"
              >
                {lbl}
              </span>
            ))}
            {labels.length > 2 && (
              <span className="text-[10px] text-gray-400 dark:text-slate-500 font-medium">+{labels.length - 2}</span>
            )}
          </div>
        </td>
      )}

      {/* 5. Assignee */}
      {cols.assignee && (
        <td
          onClick={(e) => e.stopPropagation()}
          style={{
            width: columnWidths?.assignee ? `${columnWidths.assignee}px` : undefined,
            minWidth: columnWidths?.assignee ? `${columnWidths.assignee}px` : undefined,
          }}
          className="px-3 py-2 text-[13px] font-normal text-[#172B4D] dark:text-slate-200 truncate align-middle"
        >
          <select
            value={issue.assignee_id || ''}
            onChange={(e) => {
              e.stopPropagation()
              const val = e.target.value
              onUpdate?.({ assignee_id: val || null })
            }}
            className="w-full bg-transparent hover:bg-slate-100 dark:hover:bg-slate-800 text-xs text-[#172B4D] dark:text-slate-200 border border-transparent hover:border-slate-200 dark:hover:border-slate-700 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer truncate"
          >
            <option value="" className="text-slate-500 dark:bg-slate-900">Unassigned</option>
            {members.map((m) => (
              <option key={m.id} value={m.id} className="text-slate-800 dark:bg-slate-900 dark:text-slate-100">
                {m.name || m.display_name?.split(' — ')[0] || m.display_name}
              </option>
            ))}
            {issue.assignee_id && !members.some((m) => m.id === issue.assignee_id) && (
              <option value={issue.assignee_id} className="text-slate-800 dark:bg-slate-900 dark:text-slate-100">
                {assigneeName}
              </option>
            )}
          </select>
        </td>
      )}

      {/* 6. Reporter */}
      {cols.reporter && (
        <td
          style={{
            width: columnWidths?.reporter ? `${columnWidths.reporter}px` : undefined,
            minWidth: columnWidths?.reporter ? `${columnWidths.reporter}px` : undefined,
          }}
          className="px-3 py-3 text-[13px] font-normal text-[#42526E] dark:text-slate-400 truncate align-middle"
        >
          {reporterName}
        </td>
      )}

      {/* 7. Priority */}
      {cols.priority && (
        <td
          style={{
            width: columnWidths?.priority ? `${columnWidths.priority}px` : undefined,
            minWidth: columnWidths?.priority ? `${columnWidths.priority}px` : undefined,
          }}
          className="px-3 py-3 align-middle"
        >
          <span className="inline-block rounded px-1.5 py-0.5 text-[10px] font-medium border border-blue-200 bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800">
            {priorityLabel}
          </span>
        </td>
      )}

      {/* 8. Status */}
      {cols.status && (
        <td
          onClick={(e) => e.stopPropagation()}
          style={{
            width: columnWidths?.status ? `${columnWidths.status}px` : undefined,
            minWidth: columnWidths?.status ? `${columnWidths.status}px` : undefined,
          }}
          className="px-3 py-2 align-middle"
        >
          <select
            value={issue.status}
            onChange={(e) => {
              e.stopPropagation()
              const targetStatusId = e.target.value
              const targetStatus = statuses.find((s) => s.id === targetStatusId)
              onUpdate?.({
                status: targetStatusId,
                status_category: targetStatus?.category || 'todo',
              })
            }}
            className={`w-full text-[11px] font-semibold rounded px-2 py-1 border transition-colors cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-500 ${
              statusCategory === 'done'
                ? 'border-emerald-200 bg-[#e3fcef] text-[#00875a] dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800'
                : statusCategory === 'in_progress'
                ? 'border-blue-200 bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800'
                : 'border-gray-200 bg-gray-100 text-gray-700 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700'
            }`}
          >
            {statuses.map((s) => (
              <option key={s.id} value={s.id} className="bg-white text-slate-800 dark:bg-slate-900 dark:text-slate-100">
                {s.name}
              </option>
            ))}
            {!statuses.some((s) => s.id === issue.status) && (
              <option value={issue.status} className="bg-white text-slate-800 dark:bg-slate-900 dark:text-slate-100">
                {statusName}
              </option>
            )}
          </select>
        </td>
      )}

      {/* 9. Updated */}
      {cols.updated && (
        <td
          style={{
            width: columnWidths?.updated ? `${columnWidths.updated}px` : undefined,
            minWidth: columnWidths?.updated ? `${columnWidths.updated}px` : undefined,
          }}
          className="px-3 py-2.5 text-[13px] font-normal text-[#172B4D] dark:text-slate-200 border-r border-[#EBECF0] dark:border-slate-800 align-middle whitespace-nowrap"
        >
          {updatedText}
        </td>
      )}

      {/* 10. Due Date */}
      {cols.dueDate && (
        <td
          style={{
            width: columnWidths?.dueDate ? `${columnWidths.dueDate}px` : undefined,
            minWidth: columnWidths?.dueDate ? `${columnWidths.dueDate}px` : undefined,
          }}
          className="px-3 py-2.5 text-[13px] font-normal text-[#172B4D] dark:text-slate-200 align-middle whitespace-nowrap"
        >
          {dueDateText !== '—' ? (
            <span className="inline-flex items-center gap-1.5">
              <span>{dueDateText}</span>
              {isOverdue && (
                <span title="Overdue" className="inline-flex items-center text-[#DE350B]">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0 stroke-[2.2]" />
                </span>
              )}
            </span>
          ) : (
            <span className="text-[#7A869A] text-sm font-normal pl-2">—</span>
          )}
        </td>
      )}

      {/* 11. Created */}
      {cols.created && (
        <td
          style={{
            width: columnWidths?.created ? `${columnWidths.created}px` : undefined,
            minWidth: columnWidths?.created ? `${columnWidths.created}px` : undefined,
          }}
          className="px-3 py-3 text-[13px] font-normal text-[#42526E] dark:text-slate-400 align-middle hidden md:table-cell"
        >
          {createdDate}
        </td>
      )}

      {/* 12. Actions */}
      <td className="w-[44px] min-w-[44px] max-w-[44px] px-2 py-2 align-middle text-center">
        <IssueActionsMenu
          onEdit={onOpen}
          onDelete={() => onDelete?.(issue.id)}
          onLogTime={onLogTime ? () => onLogTime(issue.id) : undefined}
        />
      </td>
    </tr>
  )
}
